import React from 'react'
import { Control, Controller } from 'react-hook-form'
import { Alert, Collapse, Typogaphy, FormItem } from 'vnetwork-ui'
import { TFunction } from 'i18next'
import dayjs from 'dayjs'

import {
    allowsAllEmployeeCompanions,
    BUSINESS_TYPE,
    MEETING_PURPOSE,
} from '../../types/utils'
import { getCustomerOptionsAsync } from '../../services'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import AsyncSelectWithServer from '@/components/Form/Shared/AsyncSelectWithServer'
import { TextareaAutoResize } from '@/components/ui/textarea'
import BusinessTimer, { TimeEntry } from './BusinessTimer'
import { DEPARTMENT_IDS } from '@/constants/departments'

interface BusinessCreateFormProps {
    control: Control<any>
    t: TFunction<'business'>
    selectedBusinessType: string
    selectedMeetingPurpose?: string
    customerIdSearch?: number
    journeyId: string | null
    handleNoOptionCustomer: (inputValue: string) => void
    timeEntries: TimeEntry[]
    setTimeEntries: React.Dispatch<React.SetStateAction<TimeEntry[]>>
    handleAddDay: () => void
    minDate?: dayjs.Dayjs
}

/**
 * Business Create Form - contains all form fields
 */
export const BusinessCreateForm: React.FC<BusinessCreateFormProps> = ({
    control,
    t,
    selectedBusinessType,
    selectedMeetingPurpose,
    customerIdSearch,
    journeyId,
    handleNoOptionCustomer,
    timeEntries,
    setTimeEntries,
    handleAddDay,
    minDate,
}) => {
    const companionDepartmentIds = allowsAllEmployeeCompanions(
        selectedMeetingPurpose
    )
        ? []
        : [
              DEPARTMENT_IDS.TECHNOLOGY,
              DEPARTMENT_IDS.DEVELOPMENT,
              DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
          ]

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 m-4">
            {/* Left column - Type, Meeting details, Place */}
            <div className="lg:col-span-6 lg:space-y-2">
                <div
                    id="business_type"
                    className="grid grid-cols-1 md:grid-cols-4 gap-2"
                >
                    {/* Business Type */}
                    <div className="col-span-1">
                        <Typogaphy size="small" className="mt-2">
                            {t('type-label')}{' '}
                            <span className="text-red-500 ml-1">*</span>
                        </Typogaphy>
                    </div>
                    <div className="col-span-1 md:col-span-3 [&_[data-ui-component=RadioGroup]]:flex-col md:[&_[data-ui-component=RadioGroup]]:flex-row">
                        <FormItem
                            control={control}
                            disabled={!!journeyId && !!customerIdSearch}
                            name="businesses.business_type"
                            type="radiogroup"
                            direction="row"
                            options={BUSINESS_TYPE(t)}
                        />
                    </div>

                    {/* Meeting Details (collapsible) */}
                    <div className="col-span-1 md:col-span-4">
                        <Collapse
                            maxHeight={0}
                            open={selectedBusinessType === 'meeting'}
                            className="transition-all duration-300"
                        >
                            {/* Notice */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                <div className="col-span-1" />
                                <div className="col-span-1 md:col-span-3 mb-2">
                                    <Alert variant="info">
                                        {t('business-meeting-notice')}
                                    </Alert>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                {/* Customer Select */}
                                <div className="col-span-1" />
                                <div className="col-span-1 md:col-span-3 pt-2">
                                    <AsyncSelectWithServer
                                        control={control}
                                        asyncOptions={getCustomerOptionsAsync(
                                            customerIdSearch?.toString() || ''
                                        )}
                                        name="businesses.customer_id"
                                        queryKey={customerIdSearch}
                                        noOptionHandler={handleNoOptionCustomer}
                                        label={t('select-customer')}
                                        allowClear={false}
                                        placeholder={t('select-customer')}
                                    />
                                </div>

                                {/* Meeting Purpose */}
                                <div className="col-span-1">
                                    <Typogaphy size="small" className="mt-2">
                                        {t('meeting-purpose-label')}
                                    </Typogaphy>
                                </div>
                                <FormItem
                                    className="col-span-1 md:col-span-3"
                                    control={control}
                                    name="businesses.meeting_purpose"
                                    type="select"
                                    options={MEETING_PURPOSE(t)}
                                    allowClear={false}
                                    placeholder={t(
                                        'meeting-purpose-placeholder'
                                    )}
                                />

                                {/* Companions */}
                                <div className="col-span-1">
                                    <Typogaphy size="small" className="mt-2">
                                        {t('companions-label')}
                                    </Typogaphy>
                                </div>
                                <div className="col-span-1 md:col-span-3">
                                    <EmployeeSelect
                                        key={
                                            companionDepartmentIds.length
                                                ? 'restricted-departments'
                                                : 'all-departments'
                                        }
                                        control={control}
                                        name="businesses.companions"
                                        multiple
                                        departmentIds={companionDepartmentIds}
                                        onlyActive
                                        label="companions-label"
                                        placeholder={t('companions-label')}
                                        showLabel={false}
                                    />
                                </div>
                            </div>
                        </Collapse>
                    </div>

                    {/* Business Place */}
                    <div className="col-span-1">
                        <Typogaphy size="small">
                            {t('business-place-label')}
                            <span className="text-red-500 ml-1">*</span>
                        </Typogaphy>
                    </div>
                    <FormItem
                        control={control}
                        className="col-span-1 md:col-span-3"
                        name="businesses.business_place"
                        type="textfield"
                        label={t('business-place-placeholder')}
                    />
                </div>
            </div>

            {/* Right column - Content */}
            <div className="lg:col-span-6">
                <div className="lg:flex mt-1 lg:items-start">
                    <Typogaphy size="small" className="mt-1">
                        {t('content-label')}
                        <span className="text-red-500 ml-1">*</span>
                    </Typogaphy>
                    <div
                        id="business_content"
                        className="flex-1 lg:mt-1 mt-2 lg:ml-4"
                    >
                        <Controller
                            control={control}
                            name="businesses.business_content"
                            render={({ field }) => (
                                <TextareaAutoResize
                                    {...field}
                                    label={t('content-placeholder')}
                                    placeholder={t('content-placeholder')}
                                    minRows={4}
                                    maxRows={10}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            {/* Time entries */}
            <div
                id="business_startdate"
                className="bg-white rounded-md col-span-12"
            >
                <BusinessTimer
                    timeEntries={timeEntries}
                    setTimeEntries={setTimeEntries}
                    handleAddDay={handleAddDay}
                    minDate={minDate}
                />
            </div>
        </div>
    )
}
